import React, {useState, useEffect, useCallback, useRef} from 'react'
import {motion} from 'framer-motion'
import SafeIcon from '../../common/SafeIcon'
import ContentGenerator from './ContentGenerator'
import StatusDropdown from './StatusDropdown'
import EmailExtractor from './EmailExtractor'
import {SupabaseService} from '../../services/supabaseService'
import * as FiIcons from 'react-icons/fi'

const {FiExternalLink, FiTag, FiCopy, FiCheck, FiDownload, FiInfo, FiChevronUp, FiSettings, FiChevronDown, FiAlertCircle, FiChevronRight, FiMinusCircle, FiPlusCircle, FiSend, FiMail, FiLoader, FiDatabase, FiX, FiEdit3} = FiIcons

function rescaleScore(tavilyScore) {
  const newMin = 60;
  const newMax = 100;
  const curve = Math.sqrt(tavilyScore / 100); // boosts mid-range scores
  return Math.round(newMin + curve * (newMax - newMin));
}

const MAX_PROMPT_WORDS = 225

const ResultsDisplay = ({results, keyword, user, searchId, onStatusesUpdated, onContentGenerated, onPurchaseClick, setActiveTab}) => {
  const [groupedResults, setGroupedResults] = useState({})
  const [copiedId, setCopiedId] = useState(null)
  const [generatedContent, setGeneratedContent] = useState({})
  const [generatedSubjects, setGeneratedSubjects] = useState({})
  const [statusMap, setStatusMap] = useState({})
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(true)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const [personalizationData, setPersonalizationData] = useState({
    custom_name: '',
    custom_title: '',
    custom_company: '',
    custom_email: '',
    custom_website_url: '',
    webhook_url: '',
    custom_prompt: ''
  })
  const [isSavingPersonalization, setIsSavingPersonalization] = useState(false)
  const [personalizationSuccess, setPersonalizationSuccess] = useState(false)
  const [defaultValues, setDefaultValues] = useState({})
  const [isOpen, setIsOpen] = useState(false)
  const [personalizationWarning, setPersonalizationWarning] = useState(false)
  const [collapsedSections, setCollapsedSections] = useState({})
  const [collapsedRows, setCollapsedRows] = useState({})
  const [toastVisible, setToastVisible] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [toastType, setToastType] = useState("success") // "success" or "error"
  const [contentSaving, setContentSaving] = useState({})
  const [relatedKeywords, setRelatedKeywords] = useState([])
  const [extractedEmails, setExtractedEmails] = useState({})
  const [sendingEmails, setSendingEmails] = useState({})
  const [tooltipVisible, setTooltipVisible] = useState({})
  const [ctaDismissed, setCtaDismissed] = useState(false)
  const [hasDirectoryAccess, setHasDirectoryAccess] = useState(false)
  const [emailLogs, setEmailLogs] = useState({}) // { [resultId]: [{to_email, subject, sent_at}] }
  const [logModal, setLogModal] = useState({ open: false, resultId: null })
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false)
  const [showPromptTooltip, setShowPromptTooltip] = useState(false)
  const [scoreTooltipVisible, setScoreTooltipVisible] = useState({})
  const [promptTooLong, setPromptTooLong] = useState(false)
  
  // Inline edit state for result Title and URL
  const [isEditingResult, setIsEditingResult] = useState({}) // { [resultId]: boolean }
  const [editValues, setEditValues] = useState({}) // { [resultId]: { title, url } }
  const [savingEdits, setSavingEdits] = useState({}) // { [resultId]: boolean }
  
  const resultsRef = useRef(null)
  const categoryRefs = useRef({})
  const textareaRefs = useRef({})
  const userExpandedRef = useRef(false)
  const isOpenInitializedRef = useRef(false)

  useEffect(() => {
    const checkDirectory = async () => {
      try {
        const access = await SupabaseService.checkDirectoryAccess(user.id)
        setHasDirectoryAccess(!!access)
      } catch (e) {
        // non-blocking
      }
    }
    if (user?.id) checkDirectory()
  }, [user?.id])

  // Track custom prompt word count and over-limit state
  useEffect(() => {
    const text = (personalizationData.custom_prompt || '').trim()
    const count = text === '' ? 0 : text.split(/\s+/).filter(Boolean).length
    setPromptTooLong(count > MAX_PROMPT_WORDS)
  }, [personalizationData.custom_prompt])

  const loadBacklinkStatuses = useCallback(async () => {
    console.log('Loading backlink statuses for search:', searchId)
    setIsLoadingStatuses(true)
    try {
      // Keep existing single-search fetch for detailed view
      const statuses = await SupabaseService.getAllBacklinkStatuses(user.id, searchId)
      console.log('Loaded statuses:', statuses)
      setStatusMap(statuses)
    } catch (error) {
      console.error('Failed to load backlink statuses:', error)
    } finally {
      setIsLoadingStatuses(false)
    }
  }, [searchId, user.id])

  const loadExtractedEmails = useCallback(async () => {
    try {
      const emails = await SupabaseService.getExtractedEmails(user.id, searchId)
      console.log('Loaded extracted emails:', emails)
      
      // Create a map of result_id to emails array
      const emailsMap = {}
      emails.forEach(item => {
        emailsMap[item.result_id] = item.emails || []
      })
      
      setExtractedEmails(emailsMap)
      
      // Update results with extracted emails
      setGroupedResults(prev => {
        const updated = {...prev}
        Object.keys(updated).forEach(category => {
          updated[category] = updated[category].map(result => {
            const emailData = emails.find(e => e.result_id === String(result.id))
            if (emailData) {
              return {...result, extracted_emails: emailData.emails}
            }
            return result
          })
        })
        return updated
      })
    } catch (error) {
      console.error('Failed to load extracted emails:', error)
    }
  }, [user.id, searchId])

  const loadGeneratedContent = useCallback(async () => {
    try {
      const content = await SupabaseService.getGeneratedContent(user.id, searchId)
      console.log('Loaded generated content:', content)
      
      // Convert to maps for easier lookup
      const contentMap = {}
      const subjectMap = {}
      content.forEach(item => {
        contentMap[item.result_id] = item.content
        subjectMap[item.result_id] = item.subject || "No subject line detected"
      })
      
      setGeneratedContent(contentMap)
      setGeneratedSubjects(subjectMap)
    } catch (error) {
      console.error('Failed to load generated content:', error)
    }
  }, [user.id, searchId])
  
  // Load email logs for this search
  const loadEmailLogs = useCallback(async () => {
    try {
      const logs = await SupabaseService.getEmailLogsForSearch(user.id, searchId)
      const map = {}
      logs.forEach(l => {
        const rid = l.result_id
        if (!map[rid]) map[rid] = []
        map[rid].push({ to_email: l.to_email, subject: l.subject, sent_at: l.sent_at })
      })
      setEmailLogs(map)
    } catch (e) {
      console.error('Failed to load email logs:', e)
    }
  }, [user.id, searchId])
  
  // Load personalization data for this search
  const loadPersonalizationData = useCallback(async () => {
    try {
      // First load default values
      const userData = await SupabaseService.getUserPersonalization(user.id, null);
      setDefaultValues({
        name: userData.name || '',
        title: userData.title || '',
        company: userData.company || '',
        email: userData.email || '',
        website: userData.website || '',
        webhook_url: userData.webhook_url || ''
      });
      
      // Then try to load search-specific values
      if (searchId) {
        const {data, error} = await SupabaseService.supabase
          .from('searches')
          .select('custom_name, custom_title, custom_company, custom_email, custom_website_url, custom_prompt')
          .eq('id', searchId)
          .single();
          
        if (!error && data) {
          // Use search-specific values if they exist and initialize open state once based on missing variables
          const loaded = {
            custom_name: data.custom_name || userData.name || '',
            custom_title: data.custom_title || userData.title || '',
            custom_company: data.custom_company || userData.company || '',
            custom_email: data.custom_email || userData.email || '',
            custom_website_url: data.custom_website_url || userData.website || '',
            custom_prompt: data.custom_prompt || ''
          }
          setPersonalizationData({
            ...loaded,
            webhook_url: userData.webhook_url || ''
          });
          if (!isOpenInitializedRef.current) {
            const hasEmptyField = [loaded.custom_name, loaded.custom_title, loaded.custom_company, loaded.custom_email, loaded.custom_website_url].some(v => !v)
            setIsOpen(hasEmptyField)
            isOpenInitializedRef.current = true
          }
        } else {
          // Use defaults if no search-specific values and initialize open state once based on missing variables
          const loaded = {
            custom_name: userData.name || '',
            custom_title: userData.title || '',
            custom_company: userData.company || '',
            custom_email: userData.email || '',
            custom_website_url: userData.website || '',
            custom_prompt: ''
          }
          setPersonalizationData({
            ...loaded,
            webhook_url: userData.webhook_url || ''
          });
          if (!isOpenInitializedRef.current) {
            const hasEmpty = [loaded.custom_name, loaded.custom_title, loaded.custom_company, loaded.custom_email, loaded.custom_website_url].some(v => !v)
            setIsOpen(hasEmpty)
            isOpenInitializedRef.current = true
          }
        }
      }
    } catch (error) {
      console.error('Failed to load personalization data:', error);
    }
  }, [user.id, searchId]);

  const loadRelatedKeywords = useCallback(async () => {
    try {
      // Generate related keywords based on the current keyword
      // In a real app, this might come from an API or be part of the search results
      // Here we'll generate some simple variations
      const baseKeyword = keyword.toLowerCase().trim();
      
      // Generate some related keywords
      const variations = [
        `${baseKeyword} guide`,
        `best ${baseKeyword}`,
        `${baseKeyword} tips`,
        `${baseKeyword} examples`,
        `${baseKeyword} resources`,
        `how to ${baseKeyword}`,
        `${baseKeyword} tutorial`,
        `${baseKeyword} ideas`,
      ];
      
      // Shuffle and take 5
      const shuffled = [...variations].sort(() => 0.5 - Math.random());
      setRelatedKeywords(shuffled.slice(0, 5));
    } catch (error) {
      console.error('Failed to load related keywords:', error);
    }
  }, [keyword]);

  const savePersonalizationData = async () => {
    if (!searchId) return;
    // Enforce 225-word limit on custom prompt
    const text = (personalizationData.custom_prompt || '').trim()
    const count = text === '' ? 0 : text.split(/\s+/).filter(Boolean).length
    if (count > MAX_PROMPT_WORDS) {
      setToastType("error")
      setToastMessage(`Custom prompt is limited to ${MAX_PROMPT_WORDS} words. Please shorten it to save.`)
      setToastVisible(true)
      return
    }
    
    setIsSavingPersonalization(true);
    
    try {
      const {error} = await SupabaseService.supabase
        .from('searches')
        .update({
          custom_name: personalizationData.custom_name,
          custom_title: personalizationData.custom_title,
          custom_company: personalizationData.custom_company,
          custom_email: personalizationData.custom_email,
          custom_website_url: personalizationData.custom_website_url,
          custom_prompt: personalizationData.custom_prompt
        })
        .eq('id', searchId);
        
      if (error) throw error;
      
      // Show success message and clear after 3 seconds
      setPersonalizationSuccess(true);
      setTimeout(() => {
        setPersonalizationSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving personalization data:', error);
    } finally {
      setIsSavingPersonalization(false);
    }
  };

  const handlePersonalizationChange = (e) => {
    const {name, value} = e.target;
    setPersonalizationData(prev => ({...prev, [name]: value}));
    
    // Clear success message when form is modified
    if (personalizationSuccess) {
      setPersonalizationSuccess(false);
    }
    
    // Check if any field is empty after change
    checkPersonalizationFields({...personalizationData, [name]: value});
  };

  const checkPersonalizationFields = (data = null) => {
    // Only consider outreach personalization fields, not webhook_url
    const fieldsToCheck = data || personalizationData;
    const {custom_name, custom_title, custom_company, custom_email, custom_website_url} = fieldsToCheck;
    const hasEmptyField = [custom_name, custom_title, custom_company, custom_email, custom_website_url].some(value => !value);
    setPersonalizationWarning(hasEmptyField);
  };

  const toggleSectionCollapse = (category) => {
    setCollapsedSections(prev => ({...prev, [category]: !prev[category]}));
  };

  const toggleRowCollapse = async (resultId) => {
    setCollapsedRows(prev => {
      const next = !prev[resultId];
      // Persist is_open: false when collapsed, true when expanded
      const isOpen = !next;
      SupabaseService.saveRowOpenState(user.id, searchId, String(resultId), isOpen).catch(() => {});
      return {...prev, [resultId]: next};
    });
  };

  const handleRowBackgroundClick = (event, resultId) => {
    const interactiveSelector = 'a, button, input, textarea, select, [role="button"], [contenteditable="true"], .no-row-toggle';
    if (event.target.closest && event.target.closest(interactiveSelector)) {
      return;
    }
    toggleRowCollapse(resultId);
  };

  const updateTextareaHeight = (element) => {
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = (element.scrollHeight) + 'px';
  };

  // When rows are expanded, auto-adjust the message/subject textareas
  useEffect(() => {
    Object.entries(collapsedRows).forEach(([id, isCollapsed]) => {
      if (!isCollapsed) {
        const contentRef = textareaRefs.current[`content-${id}`];
        const subjectRef = textareaRefs.current[`subject-${id}`];
        if (contentRef) updateTextareaHeight(contentRef);
        if (subjectRef) updateTextareaHeight(subjectRef);
      }
    });
  }, [collapsedRows]);

  useEffect(() => {
    const grouped = results.reduce((acc, result, index) => {
      const category = result.category || 'Other'
      if (!acc[category]) {
        acc[category] = []
      }
      acc[category].push({...result, id: index, content: undefined})
      return acc
    }, {})
    
    // Ensure Niche Collaborations appears first if it exists
    const orderedGrouped = {};
    if (grouped['Niche Collaborations']) {
      orderedGrouped['Niche Collaborations'] = grouped['Niche Collaborations'];
    }
    
    // Add other categories
    Object.keys(grouped).forEach(category => {
      if (category !== 'Niche Collaborations') {
        orderedGrouped[category] = grouped[category];
      }
    });
    
    setGroupedResults(orderedGrouped);
    // Default all rows to collapsed on first load until user expands
    setCollapsedRows(prev => {
      const defaultCollapsed = {};
      Object.keys(orderedGrouped).forEach(cat => {
        orderedGrouped[cat].forEach(r => {
          if (typeof prev[r.id] === 'undefined') {
            defaultCollapsed[r.id] = true;
          }
        });
      });
      return { ...defaultCollapsed, ...prev };
    });
    loadBacklinkStatuses();
    loadExtractedEmails();
    loadGeneratedContent();
    loadPersonalizationData();
    loadRelatedKeywords();
    loadEmailLogs();
    // Load persisted row open states
    (async () => {
      const openStates = await SupabaseService.getRowOpenStates(user.id, searchId);
      // Convert is_open to collapsedRows map (true => expanded => collapsed=false)
      const collapsed = {};
      Object.entries(openStates).forEach(([rid, isOpen]) => {
        collapsed[rid] = !isOpen;
      });
      setCollapsedRows(prev => ({...prev, ...collapsed}));
    })();
  }, [results, loadBacklinkStatuses, loadExtractedEmails, loadGeneratedContent, loadPersonalizationData, loadRelatedKeywords, loadEmailLogs])

  // Check personalization fields after loading data
  useEffect(() => {
    checkPersonalizationFields();
  }, [personalizationData]);

  // After a successful save, collapse the section after 1 second if the user had expanded it manually
  useEffect(() => {
    if (personalizationSuccess) {
      const timer = setTimeout(() => {
        if (userExpandedRef.current) {
          setIsOpen(false)
        }
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [personalizationSuccess])
  
  // Initialize category refs
  useEffect(() => {
    // Create refs for each category
    Object.keys(groupedResults).forEach(category => {
      if (!categoryRefs.current[category]) {
        categoryRefs.current[category] = React.createRef()
      }
    })
  }, [groupedResults])

  // Update textarea heights when content changes
  useEffect(() => {
    Object.entries(textareaRefs.current).forEach(([id, ref]) => {
      if (ref) {
        updateTextareaHeight(ref);
      }
    });
  }, [generatedContent, generatedSubjects]);
  
  // Handle scroll events for back to top button
  useEffect(() => {
    const handleScroll = () => {
      const threshold = window.matchMedia('(max-width: 640px)').matches ? 600 : 300
      if (window.scrollY > threshold) {
        setShowBackToTop(true)
      } else {
        setShowBackToTop(false)
      }
    }
    
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Auto-hide toast notification after 5 seconds
  useEffect(() => {
    let toastTimer;
    if (toastVisible) {
      toastTimer = setTimeout(() => {
        setToastVisible(false);
      }, 5000);
    }
    return () => {
      if (toastTimer) clearTimeout(toastTimer);
    };
  }, [toastVisible]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  const scrollToCategory = (category) => {
    if (categoryRefs.current[category] && categoryRefs.current[category].current) {
      categoryRefs.current[category].current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  }

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const getCategoryColor = (category) => {
    const colors = {
      'Niche Collaborations': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
      'Blog Comments': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      'Guest Posts': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      'Forum Comments': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      'Resource Pages': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      'Other': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    }
    return colors[category] || colors['Other']
  }

  const getCategoryInstructions = (category) => {
    const instructions = {
      'Niche Collaborations': "Also known as Niche Edits or Link Inserts — these are opportunities to ask for your link to be added to existing relevant articles.",
      'Blog Comments': "For these blog comment opportunities, you can leave a comment and a link back to your website.",
      'Guest Posts': "Reach out to these websites to contribute a guest post that includes links back to your website.",
      'Forum Comments': "Participate in these forum discussions and include a relevant link in your signature or reply.",
      'Resource Pages': "Submit your website to these resource pages to get a valuable backlink.",
      'Other': "Explore these opportunities to find potential places for backlinks to your website."
    }
    return instructions[category] || instructions['Other']
  }

  const handleContentGenerated = (resultId, content, subject) => {
    console.log('Content generated for result:', resultId, 'with subject:', subject)
    setGeneratedContent(prev => ({...prev, [resultId]: content}))
    setGeneratedSubjects(prev => ({...prev, [resultId]: subject}))
    
    if (onContentGenerated) {
      onContentGenerated()
    }
  }

  // Enable Send Email immediately after adding/saving emails
  const handleEmailsUpdated = (resultId, emails) => {
    setExtractedEmails(prev => ({...prev, [resultId]: emails || []}))
  }

  const handleStatusChange = async (resultId, newStatus) => {
    console.log(`Status changed for result ${resultId}: ${newStatus}`)
    setStatusMap(prev => ({...prev, [resultId]: newStatus}))
    
    // Show toast notification for submitted or live status
    if (newStatus === 'submitted' || newStatus === 'live') {
      setToastMessage("Woof! Woof! Good job hooman, let's fetch more links!");
      setToastType("success");
      setToastVisible(true);
    }
    
    if (onStatusesUpdated) {
      onStatusesUpdated()
    }
  }

  const handleContentChange = async (resultId, newContent, isSubject = false) => {
    try {
      setContentSaving(prev => ({...prev, [resultId]: true}))
      
      // Update local state
      if (isSubject) {
        setGeneratedSubjects(prev => ({...prev, [resultId]: newContent}))
      } else {
        setGeneratedContent(prev => ({...prev, [resultId]: newContent}))
      }
      
      // Get the current values to send to the API
      const content = isSubject ? generatedContent[resultId] : newContent
      const subject = isSubject ? newContent : generatedSubjects[resultId] || "No subject line detected"
      
      // Save to the database after a small debounce (500ms)
      await new Promise(resolve => setTimeout(resolve, 500))
      await SupabaseService.updateGeneratedContent(
        user.id,
        searchId,
        String(resultId),
        content,
        subject
      )
      console.log(`Content ${isSubject ? 'subject' : 'body'} updated for result:`, resultId)
    } catch (error) {
      console.error('Error saving content:', error)
    } finally {
      setContentSaving(prev => ({...prev, [resultId]: false}))
    }
  }

  // Start editing for a result (both Title and URL)
  const startEditingResult = (result) => {
    setEditValues(prev => ({
      ...prev,
      [result.id]: { title: result.title || '', url: result.url || '' }
    }))
    setIsEditingResult(prev => ({ ...prev, [result.id]: true }))
  }

  // Handle input changes while editing
  const handleEditChange = (resultId, field, value) => {
    setEditValues(prev => ({
      ...prev,
      [resultId]: { ...(prev[resultId] || { title: '', url: '' }), [field]: value }
    }))
  }

  // Save edited Title/URL to Supabase and update local UI immediately
  const saveEditedTitleUrl = async (resultId) => {
    const values = editValues[resultId] || { title: '', url: '' }
    try {
      setSavingEdits(prev => ({ ...prev, [resultId]: true }))
      await SupabaseService.updateSearchResultTitleAndUrl(user.id, searchId, resultId, values.title, values.url)
      // Update local groupedResults state
      setGroupedResults(prev => {
        const updated = { ...prev }
        Object.keys(updated).forEach(category => {
          updated[category] = updated[category].map(r => r.id === resultId ? { ...r, title: values.title, url: values.url } : r)
        })
        return updated
      })
      setIsEditingResult(prev => ({ ...prev, [resultId]: false }))
    } catch (e) {
      console.error('Failed to save edited title/url:', e)
    } finally {
      setSavingEdits(prev => ({ ...prev, [resultId]: false }))
    }
  }

  const exportToCSV = () => {
    // Create CSV headers with the required columns
    const headers = ['Type', 'Title', 'URL', 'Status', 'Email', 'Subject', 'Body']
    
    // Create CSV rows
    const csvRows = []
    csvRows.push(headers.join(','))
    
    // Process all results from all categories
    Object.entries(groupedResults).forEach(([category, categoryResults]) => {
      categoryResults.forEach(result => {
        // Get the emails as a comma-separated string
        const emails = result.extracted_emails ? result.extracted_emails.join(',') : ''
        
        // Get content and subject for this result
        const content = generatedContent[result.id] || ''
        const subject = generatedSubjects[result.id] || ''
        
        // Get status for this result
        const status = statusMap[result.id] || 'new'
        
        // Format each field with proper CSV escaping
        const formattedRow = [
          `"${(result.category || 'Other').replace(/"/g, '""')}"`, // Type
          `"${(result.title || '').replace(/"/g, '""')}"`, // Title
          `"${(result.url || '').replace(/"/g, '""')}"`, // URL
          `"${status.replace(/"/g, '""')}"`, // Status
          `"${emails.replace(/"/g, '""')}"`, // Email
          `"${subject.replace(/"/g, '""')}"`, // Subject
          `"${content.replace(/"/g, '""')}"` // Body
        ]
        
        csvRows.push(formattedRow.join(','))
      })
    })
    
    // Create CSV content
    const csvContent = csvRows.join('\n')
    
    // Create and download file
    const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'})
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `backlinks_${keyword.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleNavigateToSettings = () => {
    if (setActiveTab) {
      setActiveTab('settings');
      window.location.href = '/#/settings';
    }
  };

  const handleRelatedKeywordClick = (keyword) => {
    if (setActiveTab) {
      setActiveTab('search');
    }
    
    if (typeof onContentGenerated === 'function') {
      // This is a bit of a hack, but we're using the onContentGenerated callback
      // to notify the parent that something happened
      onContentGenerated();
    }
    
    // Start new search
    const searchEvent = new CustomEvent('startSearch', {
      detail: {keyword}
    });
    document.dispatchEvent(searchEvent);
  };
  
  // Function to check if Send Email button should be enabled for a result
const canSendEmail = (resultId) => {
  // Check if there are emails
  const hasEmails = extractedEmails[resultId] && extractedEmails[resultId].length > 0;

  // Check if there is content and subject
  const hasContent = generatedContent[resultId] && generatedContent[resultId].trim() !== '';
  const hasSubject = generatedSubjects[resultId] && generatedSubjects[resultId].trim() !== '' && generatedSubjects[resultId] !== 'No subject line detected';

  // Check if webhook URL exists
  const hasWebhookUrl = (defaultValues.webhook_url || '').trim() !== '';

  return hasEmails && hasContent && hasSubject && hasWebhookUrl;
};

// Function to get missing requirements message
const getMissingRequirements = (resultId) => {
  const missing = [];

  if (!extractedEmails[resultId]?.length) {
    missing.push("email address");
  }

  if (!generatedSubjects[resultId]?.trim() || generatedSubjects[resultId] === 'No subject line detected') {
    missing.push("subject");
  }

  if (!generatedContent[resultId]?.trim()) {
    missing.push("message");
  }

  if (!defaultValues.webhook_url?.trim()) {
    missing.push("webhook URL in settings");
  }

  if (missing.length === 0) {
    return "Ready to send email!";
  }

  return `To send email, please ensure the opportunity has ${missing.join(', ')}.`;
};
  
  // Function to handle sending emails
  const handleSendEmail = async (resultId) => {
    if (!canSendEmail(resultId)) return;
    
    // Get emails, subject, and content
    const emails = extractedEmails[resultId];
    const subject = generatedSubjects[resultId];
    const message = generatedContent[resultId];
    
    // Start sending
    setSendingEmails(prev => ({...prev, [resultId]: true}));
    
    try {
      // Track successful and failed emails
      const failedEmails = [];

      const webhookUrl = defaultValues.webhook_url;

      // Send to each email
      for (const email of emails) {
        try {
          const response = await fetch(webhookUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email,
              subject,
              message
            })
          });
          
          if (!response.ok) {
            console.error(`Failed to send email to ${email}:`, response.status);
            failedEmails.push(email);
          } else {
            const sentAtIso = new Date().toISOString();
            // Log successful send
            await SupabaseService.logSentEmail(user.id, searchId, String(resultId), email, subject, sentAtIso)
            // Update local state for logs so UI reflects immediately
            setEmailLogs(prev => ({
              ...prev,
              [resultId]: [...(prev[resultId] || []), { to_email: email, subject, sent_at: sentAtIso }]
            }))
          }
        } catch (error) {
          console.error(`Error sending email to ${email}:`, error);
          failedEmails.push(email);
        }
      }
      
      // Show toast based on success/failure
      if (failedEmails.length === 0) {
        // All emails sent successfully
        const now = new Date();
        const timeString = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        
        setToastMessage(`Sent emails to ${emails.length} contact${emails.length > 1 ? 's' : ''} at ${timeString}`);
        setToastType("success");
        setToastVisible(true);
        
        // Update status to submitted
        await SupabaseService.saveBacklinkStatus(user.id, searchId, String(resultId), 'submitted');
        setStatusMap(prev => ({...prev, [resultId]: 'submitted'}));
        
        // Do not collapse the row automatically after sending
        
      } else {
        // Some emails failed
        setToastMessage(`Failed to send to the following emails: ${failedEmails.join(', ')}. Please try again.`);
        setToastType("error");
        setToastVisible(true);
      }
    } catch (error) {
      console.error('Error sending emails:', error);
      setToastMessage("An error occurred while sending emails. Please try again.");
      setToastType("error");
      setToastVisible(true);
    } finally {
      setSendingEmails(prev => ({...prev, [resultId]: false}));
    }
  };
  
  // Function to handle tooltip visibility
  const handleTooltipHover = (resultId, isVisible) => {
    setTooltipVisible(prev => ({...prev, [resultId]: isVisible}));
  };
  
  const handleScoreTooltipHover = (resultId, isVisible) => {
    setScoreTooltipVisible(prev => ({...prev, [resultId]: isVisible}))
  };

  // Function to truncate URL
  const truncateUrl = (url, maxLength = 50) => {
    if (!url || url.length <= maxLength) return url;
    
    const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
    const domain = urlObj.hostname;
    const path = urlObj.pathname;
    
    if (domain.length + 10 >= maxLength) {
      // If domain itself is too long, truncate it
      return domain.substring(0, maxLength - 10) + '...' + domain.substring(domain.length - 7);
    }
    
    // Keep domain and truncate the middle of the path
    const pathParts = path.split('/').filter(Boolean);
    if (pathParts.length <= 2) {
      // If path is short, just show it all
      return `${domain}${path}`;
    }
    
    const firstPart = pathParts[0];
    const lastPart = pathParts[pathParts.length - 1];
    return `${domain}/${firstPart}/.../${lastPart}`;
  };

  if (Object.keys(groupedResults).length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 text-lg dark:text-gray-400">No backlink opportunities found for this keyword.</p>
      </div>
    )
  }

  const categories = Object.keys(groupedResults)
  
  return (
    <div className="space-y-8" ref={resultsRef}>
      {/* Toast notification */}
      {toastVisible && (
        <motion.div
          initial={{opacity: 0, y: -50}}
          animate={{opacity: 1, y: 0}}
          exit={{opacity: 0, y: -50}}
          className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 flex items-center ${
            toastType === "success" ? "bg-white dark:bg-gray-800" : "bg-red-50 dark:bg-red-900"
          } p-4 rounded-lg shadow-lg max-w-sm`}
        >
          <img src="https://i.imghippo.com/files/GkAk1182MIE.webp" alt="K9 Links Dog" className="w-12 h-12 mr-3" />
          <p className={`${toastType === "success" ? "text-gray-800 dark:text-white" : "text-red-600 dark:text-red-400"} font-medium`}>{toastMessage}</p>
        </motion.div>
      )}

      <div className="text-center px-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
          <h2 className="text-2xl md:text-3xl text-gray-900 font-chewy break-words dark:text-white">
            {results?.[0]?.category === 'Custom Results' ? 'Results for Custom Search' : `Results for "${keyword}"` }
          </h2>
          <div className="flex items-center gap-2 sm:gap-4 mt-2 sm:mt-0">
            <p className="text-gray-600 dark:text-gray-400">
              <b>Found {results.length}</b> backlink opportunities
            </p>
            <button onClick={exportToCSV} className="flex items-center text-[#D59A4C] hover:text-[#B58C54] font-medium transition-colors">
              <SafeIcon icon={FiDownload} className="w-4 h-4 mr-1" />
              Export to CSV
            </button>
          </div>
        </div>
        
        {/* Related Keywords Line */}
        <div className="text-left mb-4 mt-2">
          <p className="text-gray-700 dark:text-gray-300">
            <b>Related keywords:</b><br></br>
            {relatedKeywords.map((kw, idx) => (
              <React.Fragment key={kw}>
                <button 
                  onClick={() => handleRelatedKeywordClick(kw)}
                  className="text-[#D59A4C] hover:text-[#B58C54] font-small no-border"
                >
                  {kw}
                </button>
                {idx < relatedKeywords.length - 1 ? ', ' : ''}
              </React.Fragment>
            ))}
          </p>
        </div>

        {/* Outreach Personalization Section - Now Collapsible */}
        <div className="bg-white rounded-2xl shadow-lg px-2 py-0 dark:bg-gray-800">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Outreach Personalization</h3>
            <div className="flex items-center py-1">
              {personalizationSuccess && (
                <div className="text-green-600 flex items-center mr-3 dark:text-green-400">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 mr-1" />
                  <span>Saved successfully</span>
                </div>
              )}
              <button
                onClick={() => {
                  const next = !isOpen
                  setIsOpen(next)
                  if (next) {
                    userExpandedRef.current = true
                  }
                }}
                className="text-[#D59A4C] hover:text-[#B58C54] font-medium transition-colors flex items-center bg-[#D59A4C30] px-4 py-1 rounded-[40px]"
              >
                {isOpen ? (
                  <>
                    <span className="mr-1">-</span>Collapse
                  </>
                ) : (
                  <>
                    <span className="mr-1">+</span>Expand
                  </>
                )}
              </button>
            </div>
          </div>
          {isOpen && (
          <p className="text-sm text-gray-600 mb-4 dark:text-gray-400">
            These values override your default outreach settings for this specific list of opportunities.{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleNavigateToSettings();
              }}
              className="text-[#D59A4C] hover:text-[#B58C54]"
            >
              Manage your default settings
            </a>
          </p>
          )}
          {isOpen && (
            <>
              {personalizationWarning && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-3 dark:bg-red-900/30 dark:border-red-600 flex items-start">
                  <SafeIcon
                    icon={FiAlertCircle}
                    className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5 dark:text-red-400"
                  />
                  <p className="text-sm text-red-600 dark:text-red-400">
                    For best results with AI content generation, please fill out all personalization fields.
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <input
                  type="text"
                  name="custom_name"
                  value={personalizationData.custom_name}
                  onChange={handlePersonalizationChange}
                  placeholder="Name"
                  className={`px-4 py-2 border ${
                    !personalizationData.custom_name ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  } rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white`}
                />
                <input
                  type="text"
                  name="custom_title"
                  value={personalizationData.custom_title}
                  onChange={handlePersonalizationChange}
                  placeholder="Title"
                  className={`px-4 py-2 border ${
                    !personalizationData.custom_title ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  } rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white`}
                />
                <input
                  type="text"
                  name="custom_company"
                  value={personalizationData.custom_company}
                  onChange={handlePersonalizationChange}
                  placeholder="Company"
                  className={`px-4 py-2 border ${
                    !personalizationData.custom_company ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  } rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white`}
                />
                <input
                  type="email"
                  name="custom_email"
                  value={personalizationData.custom_email}
                  onChange={handlePersonalizationChange}
                  placeholder="Email"
                  className={`px-4 py-2 border ${
                    !personalizationData.custom_email ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  } rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white`}
                />
                <input
                  type="url"
                  name="custom_website_url"
                  value={personalizationData.custom_website_url}
                  onChange={handlePersonalizationChange}
                  placeholder="Website"
                  className={`px-4 py-2 border ${
                    !personalizationData.custom_website_url ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  } rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white`}
                />
              </div>
              
              <div className="mt-4 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                  className="text-[#D59A4C] hover:text-[#B58C54] font-medium no-border"
                >
                  Advanced Options {isAdvancedOpen ? '▲' : '▼'}
                </button>
                <button
                  onClick={savePersonalizationData}
                  disabled={isSavingPersonalization || promptTooLong}
                  className="flex items-center px-2 mb-1 bg-[#D59A4C] text-white rounded-lg hover:bg-[#B58C54] disabled:opacity-50 transition-colors"
                >
                  {isSavingPersonalization ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" />
                      Save
                    </>
                  )}
                </button>
              </div>

              {isAdvancedOpen && (
                <div className="mt-4">
                  <hr className="border-gray-200 dark:border-gray-700 mb-3" />
                  <p className="text-sm text-gray-600 italic dark:text-gray-400 mb-3">
                    All you need are the settings above that are used with the app's default AI prompts to generate personalized content.<br/>
                    Below are optional advanced settings for further personalizing outreach content by creating your own prompt that overrides the app's defaults.
                  </p>
                  <div className="mb-2 flex items-center text-gray-700 dark:text-gray-200">
                    <span className="mr-2 font-medium">Custom Prompt (optional)</span>
                    <div className="relative">
                      <div
                        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400 focus:outline-none no-border"
                        onMouseEnter={() => setShowPromptTooltip(true)}
                        onMouseLeave={() => setShowPromptTooltip(false)}
                        onClick={() => setShowPromptTooltip(v => !v)}
                      >
                        <SafeIcon icon={FiInfo} className="w-4 h-4 text-[#D59A4C]" />
                      </div>
                      {showPromptTooltip && (
                        <div className="fixed left-1/2 -translate-x-1/2 bottom-4 z-10 max-w-[calc(100vw-2rem)] sm:absolute sm:left-0 sm:top-full sm:bottom-auto sm:translate-x-0 sm:mt-2 sm:w-72 bg-white dark:bg-gray-800 p-2 rounded shadow-lg border border-gray-200 dark:border-gray-700 text-sm text-left break-words">
                          Overrides the default AI prompts for generating personalized outreach content for this list of opportunities.
                        </div>
                      )}
                    </div>
                  </div>
                  <textarea
                    name="custom_prompt"
                    value={personalizationData.custom_prompt}
                    onChange={handlePersonalizationChange}
                    rows={5}
                    placeholder="e.g. Write a 150 word, persuasive outreach email introducing myself as [Your Name] from [Your Company] highlighting our eco-friendly dog toys to a blog owner. Begin with an engaging subject line and address the email to Hi Team."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white dark:border-gray-600"
                  />
                  {promptTooLong && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">Custom prompt is limited to 225 words. Please shorten it to save.</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Add category navigation links */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-3 pb-3">
          <div className="flex flex-wrap justify-center gap-3 mt-2">
            <span className="text-gray-600 dark:text-gray-400 font-bold">Jump to:</span>
            {categories.map(category => (
              <button
                key={category}
                onClick={() => scrollToCategory(category)}
                className="text-[#D59A4C] hover:text-[#B58C54] font-medium no-border"
              >
                {category} ({groupedResults[category].length})
              </button>
            ))}
          </div>
        </div>
        
        {isLoadingStatuses && (
          <p className="text-sm text-blue-600 dark:text-blue-400">Loading statuses...</p>
        )}
      </div>

      {Object.entries(groupedResults).map(([category, categoryResults]) => (
        <motion.div
          key={category}
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          className="bg-white rounded-2xl shadow-lg overflow-hidden dark:bg-gray-800"
          ref={categoryRefs.current[category] || (categoryRefs.current[category] = React.createRef())}
          id={category.toLowerCase().replace(/\s+/g, '-')}
        >
          <div className="bg-[#D59A4C40] px-4 md:px-6 py-4 border-b dark:border-gray-600">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="sm:text-2xl text-lg font-bold text-gray-900 dark:text-white">{category}</h2>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(category)}`}>
                  {categoryResults.length} opportunities
                </span>
                <button
                  onClick={() => toggleSectionCollapse(category)}
                  className="text-[#D59A4C] hover:text-[#B58C54] font-medium transition-colors flex items-center"
                >
                  {collapsedSections[category] ? (
                    <>
                      <span className="mr-1">+</span>Expand
                    </>
                  ) : (
                    <>
                      <span className="mr-1">-</span>Collapse
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="mt-2 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 p-2 rounded-lg flex items-start">
              <SafeIcon icon={FiInfo} className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
              <span className="sm:text-md text-sm">{getCategoryInstructions(category)}</span>
            </div>
          </div>
          
          {!collapsedSections[category] && (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {categoryResults.map((result) => (
                <div
                  key={result.id}
                  className="p-4 md:p-6 hover:shadow-md transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                  onClick={(e) => handleRowBackgroundClick(e, result.id)}
                >
                  <div className="flex flex-col space-y-4">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-lg font-semibold text-gray-800 mb-2 break-words dark:text-white">
                          {typeof result.score !== 'undefined' && result.score !== null && !isNaN(Number(result.score)) && (
                                                          <span
                                className="mr-2 relative inline-flex items-center justify-center px-2 py-0.5 rounded bg-gray-200 text-white-100 text-lg font-medium font-chewy dark:bg-gray-600 dark:text-white-100 no-row-toggle ring-2 ring-white dark:ring-[#111827]"
                                onMouseEnter={() => handleScoreTooltipHover(result.id, true)}
                                onMouseLeave={() => handleScoreTooltipHover(result.id, false)}
                                onClick={() => handleScoreTooltipHover(result.id, !scoreTooltipVisible[result.id])}
                                style={{
                                  background: `conic-gradient(#d59a4c ${rescaleScore(Number(result.score) * 100)}%, #111827 ${rescaleScore(Number(result.score) * 100)}% 100%)`,
                                  width: '1.75rem',
                                  height: '1.75rem',
                                  borderRadius: '9999px'
                                }}
                                                            >
                               <span className="text-lg text-white">{rescaleScore(Number(result.score) * 100)}</span>
                               {scoreTooltipVisible[result.id] && (
                                   <div className="fixed left-1/2 -translate-x-1/2 bottom-4 z-10 max-w-[calc(100vw-2rem)] sm:absolute sm:left-0 sm:top-full sm:bottom-auto sm:translate-x-0 sm:mt-2 sm:w-72 bg-white dark:bg-gray-800 p-2 rounded shadow-lg border border-gray-200 dark:border-gray-700 text-sm text-left font-sans break-words">
                                     <b>AI Relevancy Score</b>: Displaying only the most relevant opportunities filtered from Google.
                                   </div>
                                 )}
                             </span>
)}
                          {isEditingResult[result.id] ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={editValues[result.id]?.title || ''}
                                onChange={(e) => handleEditChange(result.id, 'title', e.target.value)}
                                className="w-full max-w-xl px-3 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white dark:border-gray-600"
                                placeholder="Title"
                              />
                              <button
                                onClick={() => saveEditedTitleUrl(result.id)}
                                disabled={!!savingEdits[result.id]}
                                className="px-2 py-1 bg-[#D59A4C] text-white rounded-lg hover:bg-[#B58C54] disabled:opacity-50 transition-colors"
                              >
                                {savingEdits[result.id] ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                          ) : (
                            <span className="inline-flex items-center">
                              {result.title}
                              <button
                                className="ml-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 no-border"
                                onClick={() => startEditingResult(result)}
                                aria-label="Edit title and URL"
                              >
                                <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                              </button>
                            </span>
                          )}
                        </h4>
                        <div className="flex flex-col space-y-2">
                          <div className="flex items-center text-sm text-gray-600 break-all dark:text-gray-400">
                            <SafeIcon icon={FiExternalLink} className="w-4 h-4 mr-1 flex-shrink-0" />
                            {isEditingResult[result.id] ? (
                              <input
                                type="text"
                                value={editValues[result.id]?.url || ''}
                                onChange={(e) => handleEditChange(result.id, 'url', e.target.value)}
                                className="w-full max-w-xl px-3 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white dark:border-gray-600"
                                placeholder="URL"
                              />
                            ) : (
                              <>
                                <a
                                  href={result.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:text-blue-900 text-blue-600 overflow-hidden text-ellipsis dark:hover:text-blue-400 text-lg"
                                  title={result.url}
                                >
                                  {truncateUrl(result.url)}
                                </a>
                                <button
                                  className="ml-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 no-border"
                                  onClick={() => startEditingResult(result)}
                                  aria-label="Edit title and URL"
                                >
                                  <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                            <SafeIcon icon={FiTag} className="w-4 h-4 mr-1 flex-shrink-0" />
                            <span className="break-words text-lg">{result.footprint}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <EmailExtractor
                          result={result}
                          user={user}
                          searchId={searchId}
                          onPurchaseClick={onPurchaseClick}
                          onEmailsUpdated={(emails) => handleEmailsUpdated(result.id, emails)}
                        />
                        
                        <StatusDropdown
                          user={user}
                          searchId={searchId}
                          resultId={String(result.id)}
                          initialStatus={statusMap[result.id] || 'new'}
                          onStatusChange={(newStatus) => handleStatusChange(result.id, newStatus)}
                        />
                        
                        <button
                          onClick={() => toggleRowCollapse(result.id)}
                          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 rounded-full transition-colors no-border"
                          aria-label={collapsedRows[result.id] ? "Expand row" : "Collapse row"}
                        >
                          <SafeIcon
                            icon={collapsedRows[result.id] ? FiPlusCircle : FiMinusCircle}
                            className="w-5 h-5"
                          />
                        </button>
                      </div>
                    </div>
                    
                    {!collapsedRows[result.id] && (
                      <>
                        {generatedContent[result.id] && (
                          <div className="mt-4 p-4 bg-gray-50 rounded-lg dark:bg-gray-700">
                            <div className="mb-2">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Generated Content:
                              </span>
                              {contentSaving[result.id] && (
                                <span className="text-xs ml-2 text-blue-600 dark:text-blue-400">
                                  Saving...
                                </span>
                              )}
                            </div>
                            
                            {/* Subject Line (for Guest Posts, Resource Pages, and Niche Collaborations) */}
                            {(result.category === 'Guest Posts' || result.category === 'Resource Pages' || result.category === 'Niche Collaborations' || result.category === 'Custom Results') && (
                              <div className="mb-4">
                                <div className="flex items-center justify-between mb-1">
                                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                                    Subject Line:
                                  </label>
                                  <button
                                    onClick={() => copyToClipboard(generatedSubjects[result.id], `subject-${result.id}`)}
                                    className="flex items-center text-xs text-blue-600 hover:text-blue-800 px-1 py-0 dark:text-blue-400 dark:hover:text-blue-300 no-border"
                                  >
                                    <SafeIcon
                                      icon={copiedId === `subject-${result.id}` ? FiCheck : FiCopy}
                                      className="w-3 h-3 mr-1"
                                    />
                                    {copiedId === `subject-${result.id}` ? 'Copied!' : 'Copy'}
                                  </button>
                                </div>
                                <textarea
                                  ref={el => textareaRefs.current[`subject-${result.id}`] = el}
                                  value={generatedSubjects[result.id] || ''}
                                  onChange={(e) => handleContentChange(result.id, e.target.value, true)}
                                  className="w-full p-3 border border-gray-200 bg-white rounded dark:bg-gray-600 dark:border-gray-500 dark:text-white text-sm text-gray-800 dark:text-gray-200 resize-y overflow-hidden"
                                  rows={1}
                                  style={{height: 'auto', minHeight: '40px'}}
                                  onInput={(e) => updateTextareaHeight(e.target)}
                                />
                              </div>
                            )}
                            
                            {/* Message Body */}
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                                  Message Body:
                                </label>
                                <button
                                  onClick={() => copyToClipboard(generatedContent[result.id], `content-${result.id}`)}
                                  className="flex items-center text-xs text-blue-600 hover:text-blue-800 px-1 py-0 dark:text-blue-400 dark:hover:text-blue-300 no-border"
                                >
                                  <SafeIcon
                                    icon={copiedId === `content-${result.id}` ? FiCheck : FiCopy}
                                    className="w-3 h-3 mr-1"
                                  />
                                  {copiedId === `content-${result.id}` ? 'Copied!' : 'Copy'}
                                </button>
                              </div>
                              <textarea
                                ref={el => textareaRefs.current[`content-${result.id}`] = el}
                                value={generatedContent[result.id] || ''}
                                onChange={(e) => handleContentChange(result.id, e.target.value)}
                                className="w-full p-3 border border-gray-200 bg-white rounded dark:bg-gray-600 dark:border-gray-500 dark:text-white text-sm text-gray-800 dark:text-gray-200 resize-y min-h-[100px] overflow-hidden"
                                style={{height: 'auto'}}
                                onInput={(e) => updateTextareaHeight(e.target)}
                              />
                            </div>
                          </div>
                        )}
                        
                        <div className="mt-4">
                          <div className="flex items-center gap-3">
                            <ContentGenerator
                              result={result}
                              keyword={keyword}
                              user={user}
                              searchId={searchId}
                              onContentGenerated={(content, subject) => handleContentGenerated(result.id, content, subject)}
                              hasContent={!!generatedContent[result.id]}
                              onPurchaseClick={onPurchaseClick}
                              actionsRight={
                                (!generatedContent[result.id] && (result.category === 'Guest Posts' || result.category === 'Resource Pages' || result.category === 'Niche Collaborations' || result.category === 'Custom Results')) ? (
                                  <div className="flex items-center ml-1">
                                    <button
                                      onClick={() => handleSendEmail(result.id)}
                                      disabled={!canSendEmail(result.id) || sendingEmails[result.id]}
                                      className="flex items-center px-2 py-0 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors no-border blue-btn"
                                    >
                                      <SafeIcon 
                                        icon={sendingEmails[result.id] ? FiLoader : FiMail} 
                                        className={`hidden sm:inline w-4 h-4 sm:mr-2 ${sendingEmails[result.id] ? 'animate-spin' : ''}`} 
                                      />
                                      {sendingEmails[result.id] ? 'Sending...' : 'Send Email'}
                                    </button>
                                    {!canSendEmail(result.id) && (
                                      <div className="pl-3 relative">
                                        <div 
                                          className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400 focus:outline-none no-border no-row-toggle"
                                          onMouseEnter={() => handleTooltipHover(result.id, true)}
                                          onMouseLeave={() => handleTooltipHover(result.id, false)}
                                          onClick={() => handleTooltipHover(result.id, !tooltipVisible[result.id])}
                                        >
                                          <SafeIcon icon={FiInfo} className="w-5 h-5" />
                                        </div>
                                        {tooltipVisible[result.id] && (
                                          <div className="fixed left-1/2 -translate-x-1/2 bottom-4 z-10 max-w-[calc(100vw-2rem)] sm:absolute sm:left-0 sm:top-full sm:bottom-auto sm:translate-x-0 sm:mt-2 sm:w-72 bg-white dark:bg-gray-800 p-2 rounded shadow-lg border border-gray-200 dark:border-gray-700 text-sm text-left break-words">
                                            {getMissingRequirements(result.id)}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ) : null
                              }
                            />
                            
                            {generatedContent[result.id] && (result.category === 'Guest Posts' || result.category === 'Resource Pages' || result.category === 'Niche Collaborations' || result.category === 'Custom Results') && (
                            <div className="flex items-center">
                              {/* Send Email Button - Positioned after Save Content button with same padding */}
                              <button
                                onClick={() => handleSendEmail(result.id)}
                                disabled={!canSendEmail(result.id) || sendingEmails[result.id]}
                                className="flex items-center px-1 py-0 sm:px-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors no-border blue-btn"
                              >
                                <SafeIcon 
                                  icon={sendingEmails[result.id] ? FiLoader : FiMail} 
                                  className={`hidden sm:inline w-4 h-4 sm:mr-2 ${sendingEmails[result.id] ? 'animate-spin' : ''}`} 
                                />
                                {sendingEmails[result.id] ? 'Sending...' : 'Send Email'}
                              </button>
                              
                              {/* Info Icon and Tooltip - Positioned immediately right of button with 3px left padding */}
                              {!canSendEmail(result.id) && (
                                <div className="pl-3 relative">
                                  <div 
                                    className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400 focus:outline-none no-border no-row-toggle"
                                    onMouseEnter={() => handleTooltipHover(result.id, true)}
                                    onMouseLeave={() => handleTooltipHover(result.id, false)}
                                    onClick={() => handleTooltipHover(result.id, !tooltipVisible[result.id])}
                                  >
                                    <SafeIcon icon={FiInfo} className="w-5 h-5" />
                                  </div>
                                  
                                  {tooltipVisible[result.id] && (
                                    <div className="fixed left-1/2 -translate-x-1/2 bottom-4 z-10 max-w-[calc(100vw-2rem)] sm:absolute sm:left-0 sm:top-full sm:bottom-auto sm:translate-x-0 sm:mt-2 sm:w-72 bg-white dark:bg-gray-800 p-2 rounded shadow-lg border border-gray-200 dark:border-gray-700 text-sm text-left break-words">
                                      {getMissingRequirements(result.id)}
                                    </div>
                                  )}
                                </div>
                              )}
                              {emailLogs[result.id]?.length > 0 && (
                                <button
                                  onClick={() => setLogModal({ open: true, resultId: result.id })}
                                  className="flex items-center text-[#D59A4C] hover:text-[#B58C54] ml-3 no-border"
                                >
                                  <SafeIcon icon={FiDatabase} className="w-4 h-4 mr-1" />
                                  <span className="text-sm font-medium">Email Log</span>
                                </button>
                              )}
                            </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      ))}

      {/* Email Logs Modal */}
      {logModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black opacity-40" onClick={() => setLogModal({ open: false, resultId: null })}></div>
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-lg w-full mx-4 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Email Log</h3>
              <button className="no-border" onClick={() => setLogModal({ open: false, resultId: null })} aria-label="Close">
                <SafeIcon icon={FiX} className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {(emailLogs[logModal.resultId] || []).map((entry, idx) => (
                <div key={idx} className="p-2 rounded border border-gray-200 dark:border-gray-700">
                  <div className="text-sm text-gray-600 dark:text-gray-300">{new Date(entry.sent_at).toLocaleString()}</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">{entry.to_email}</div>
                  <div className="text-sm text-gray-700 dark:text-gray-200">{entry.subject}</div>
                </div>
              ))}
              {!(emailLogs[logModal.resultId]?.length) && (
                <div className="text-sm text-gray-600 dark:text-gray-300">No emails logged yet.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Back to top button */}
      {showBackToTop && (
        <>
          {!ctaDismissed && !hasDirectoryAccess && (
          <div className="fixed bottom-24 left-6 z-50 max-w-xs w-[320px] bg-white dark:bg-gray-800 shadow-xl rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="flex items-start justify-between">
              <div className="text-md text-gray-800 dark:text-gray-100 leading-snug">
                <span className="font-semibold">✅ Found: {results.length} Backlink Opportunities</span>
                <span className="block mt-1"><span>Now, one-click generate outreach content and scrape email contacts to send your linkbuilding pitches.</span><br/><br/>
                                             <span className="text-md font-semibold">🏆 1,000s of Hand Picked Links</span><br/>
                                             <span>Save more time and find better links 100% enriched with contact info in our <span className="font-semibold">Pro Link Directory</span></span>.</span>
                                             
                <button
                  onClick={() => { setActiveTab('directory'); }}
                  className="mt-2 inline-flex items-center px-3 py-1 text-white bg-[#D59A4C] hover:bg-[#B58C54] rounded-md text-sm"
                >
                  Learn More
                </button>
              </div>
              <button
                onClick={() => setCtaDismissed(true)}
                className="ml-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 no-border"
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>
          )}
          <button
            onClick={scrollToTop}
            className="fixed bottom-[81px] right-[21px] p-3 bg-[#D59A4C] text-white rounded-full shadow-lg hover:bg-[#B58C54] transition-all z-50"
            aria-label="Back to top"
          >
            <img src="https://i.imghippo.com/files/GkAk1182MIE.webp" alt="K9 Links Dog" className="w-5 h-5" />
          </button>
        </>
      )}
    </div>
  )
}

export default ResultsDisplay
